"use client";

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { refreshCurrentJwt } from "#app/(unauthorized)/authentication/jwt";
import {
  cacheJwt,
  getJwt,
} from "#app/(unauthorized)/authentication/jwtStorageService";
import { useJwtIsImpersonated } from "#app/(unauthorized)/authentication/useJwt";
import { postChallengeComplete } from "#app/_api/bol-authentication-service/post-challenge-complete";
import {
  postMfaAnalyzeWidget,
  type PostMfaAnalyzeWidgetData,
} from "#app/_api/external-fund-transfer-service/post-mfa-analyze-widget";
import {
  checkImageWidgetPageStateMapping,
  type CheckImageWidgetPageState,
} from "#app/_mfa/check-image-widget-helpers";
import { logError } from "#app/lib/logger";
import { Button } from "#ui/button";
import {
  Dialog,
  DialogClose,
  DialogCloseIconButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  type DialogTriggerProps,
} from "#ui/dialog";
import { Icon } from "#ui/icon";

import { CheckImageWidgetModal } from "./CheckImageWidgetModal";
import { ChecksDialogContent } from "./checks-dialog-content";
import { ChecksDialogDetail } from "./checks-dialog-detail";
import type { CheckVm } from "./checks-page.utils";

type ChecksDialogIdProps = { checkData?: never; checkId: string };
type ChecksDialogDataProps = { checkData: CheckVm; checkId?: never };
type ChecksDialogProps = (ChecksDialogIdProps | ChecksDialogDataProps) & {
  children?: DialogTriggerProps["children"];
};

export function ChecksDialog({
  checkData,
  checkId,
  children,
}: ChecksDialogProps) {
  const isImpersonated = useJwtIsImpersonated();
  const [isOpen, setIsOpen] = useState(false);
  const [widgetData, setWidgetData] = useState<PostMfaAnalyzeWidgetData | null>(
    null,
  );
  const [pageState, setPageState] = useState<CheckImageWidgetPageState>(
    checkImageWidgetPageStateMapping.Idle,
  );

  const analyzeMutation = useMutation({
    mutationFn: postMfaAnalyzeWidget,
    onSuccess: (data) => {
      if (data === null) {
        // No MFA needed, proceed directly to viewing
        setPageState(checkImageWidgetPageStateMapping.Viewing);
      } else {
        // MFA required, show widget
        setWidgetData(data);
        setPageState(checkImageWidgetPageStateMapping.Widgeting);
      }
    },
    onError: (err) => {
      logError(err);
      setPageState(checkImageWidgetPageStateMapping.Error);
      toast.error("We are unable to verify MFA at this time.", {
        closeButton: true,
        duration: Infinity,
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (authVendorJwt: string) =>
      postChallengeComplete({
        JWT: getJwt(),
        AuthVendorJwt: authVendorJwt,
      }),
    onSuccess: (data) => {
      if (data.status === 3 && data.jwt) {
        // Success - cache new JWT and proceed to viewing
        cacheJwt(data.jwt);
        refreshCurrentJwt();

        setPageState(checkImageWidgetPageStateMapping.Viewing);
        setWidgetData(null);
      } else {
        setPageState(checkImageWidgetPageStateMapping.Error);
        setWidgetData(null);
        logError(
          `MFA completion failed - Status: ${data.status}, JWT exists: ${!!data.jwt}`,
        );
        toast.error("Multifactor authentication failed.", {
          closeButton: true,
          duration: Infinity,
        });
      }
    },
    onError: (err) => {
      logError(err);
      setPageState(checkImageWidgetPageStateMapping.Error);
      setWidgetData(null);
      toast.error("We are unable to complete MFA at this time.", {
        closeButton: true,
        duration: Infinity,
      });
    },
  });

  function handleWidgetSuccess(event: any) {
    if (
      pageState !== checkImageWidgetPageStateMapping.Widgeting ||
      completeMutation.isPending
    ) {
      return;
    }
    setPageState(checkImageWidgetPageStateMapping.Completing);
    completeMutation.mutate(event.access_token);
  }

  function handleWidgetError(_event: any) {
    // Leave the widget to handle its error
    // For example show a message letting the user know they entered the wrong code and should try again.
  }

  function handleWidgetCancel() {
    setPageState(checkImageWidgetPageStateMapping.Idle);
    setWidgetData(null);
    setIsOpen(false);
  }

  function handleDialogOpenChange(open: boolean) {
    setIsOpen(open);
    if (open && pageState === checkImageWidgetPageStateMapping.Idle) {
      if (isImpersonated) {
        // Skip MFA for impersonated users
        setPageState(checkImageWidgetPageStateMapping.Viewing);
      } else {
        // Dialog is opening, trigger MFA analyze
        setPageState(checkImageWidgetPageStateMapping.Analyzing);
        analyzeMutation.mutate({});
      }
    } else if (!open) {
      // Dialog is closing, reset state
      setPageState(checkImageWidgetPageStateMapping.Idle);
      setWidgetData(null);
    }
  }

  const isProcessing =
    pageState === checkImageWidgetPageStateMapping.Analyzing ||
    pageState === checkImageWidgetPageStateMapping.Completing;

  const canViewContent = pageState === checkImageWidgetPageStateMapping.Viewing;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogTrigger asChild>
          {children ?? (
            <Button
              variant="link"
              size="sm"
              className="gap-3px text-[length:inherit]"
            >
              <Icon name="ms-file-image" />
              <span>
                View <span className="sr-only">check details</span>
              </span>
            </Button>
          )}
        </DialogTrigger>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent
            variant="center-scroll-inner"
            aria-describedby={undefined}
            className="max-w-800px"
          >
            <DialogHeader>
              <DialogTitle>Check Details</DialogTitle>
            </DialogHeader>
            <DialogMain>
              {isProcessing ? (
                <div className="flex items-center justify-center py-50px">
                  <div className="text-center">
                    <p className="text-shade-60">Verifying your identity...</p>
                  </div>
                </div>
              ) : canViewContent ? (
                <>
                  {checkData ? (
                    <ChecksDialogContent data={checkData} />
                  ) : (
                    <ChecksDialogDetail checkId={checkId} />
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center py-50px">
                  <div className="text-center">
                    <p className="text-shade-60">
                      {pageState === checkImageWidgetPageStateMapping.Error
                        ? "An error occurred. Please try again."
                        : "Loading..."}
                    </p>
                  </div>
                </div>
              )}
              <DialogCloseIconButton />
            </DialogMain>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {widgetData ? (
        <CheckImageWidgetModal
          open={pageState === checkImageWidgetPageStateMapping.Widgeting}
          widgetData={widgetData}
          onSuccess={handleWidgetSuccess}
          onError={handleWidgetError}
          onCancel={handleWidgetCancel}
        />
      ) : null}
    </>
  );
}
